import { STORE_TIMEZONE } from "../filters/timezone";
import type { IsoDate, IsoDateTime, Money } from "../primitives";
import {
  bucketStart,
  bucketStarts,
  fromPaise,
  storeDate,
  toPaise,
  type OrderSeriesBucket,
} from "./series";

/**
 * Units and sales per SKU over time (#678): what Reports > Products' "SKU
 * trends" view draws, and the arithmetic the API and the fixture adapter share
 * to produce it.
 *
 * Built on the same footing as the orders series (`series.ts`): the API groups
 * in Postgres, the fixture groups in memory, and everything that has to agree
 * between them — the store's calendar day, where a week starts, the
 * zero-filled buckets, which SKUs are drawn by default, exact money — is here.
 */

/** SKUs drawn when nobody has picked any: the best sellers in the range. */
export const DEFAULT_SKU_PICKS = 5;

/** The most SKUs one chart draws; past about eight a line chart is a tangle. */
export const MAX_SKU_PICKS = 8;

/** What the view asks for. `from` and `to` are the store's calendar dates. */
export interface SkuSeriesQuery {
  /** First day counted, inclusive, in the store's timezone. */
  from: IsoDate;
  /** Last day counted, inclusive, in the store's timezone. */
  to: IsoDate;
  bucket: OrderSeriesBucket;
  /** Lines of cancelled orders are left out unless this is true. */
  includeCancelled?: boolean;
  /**
   * The SKUs to draw, by `SkuSeriesItem.key`, at most `MAX_SKU_PICKS`. Absent
   * or empty draws the `DEFAULT_SKU_PICKS` best sellers in the range.
   */
  keys?: string[];
}

/**
 * What one SKU sold in the range.
 *
 * `sku` is null for lines that have none, which group under their own title
 * and variant instead (see `skuGroupKey`).
 */
export interface SkuSeriesItem {
  key: string;
  sku: string | null;
  productTitle: string;
  /** Blank for a single-variant product. */
  variantTitle: string;
  units: number;
  /** Orders that hold at least one unit of it. */
  orders: number;
  amount: Money;
}

/** One SKU's line on a chart. */
export interface SkuSeriesLine {
  /** The `SkuSeriesItem.key` of the SKU. */
  key: string;
  /**
   * One figure per entry in `buckets`, as decimal strings (whole numbers for
   * units, money for amount) — one shape for both, and money is never a float.
   */
  values: string[];
  total: string;
}

/**
 * The view's data. Counts and sums only — no order, line or customer is in it.
 */
export interface SkuSeries {
  from: IsoDate;
  to: IsoDate;
  bucket: OrderSeriesBucket;
  /** The zone the days were cut in. */
  timezone: string;
  /** The first day of every bucket in the range, zero-filled. */
  buckets: IsoDate[];
  /**
   * Every SKU that sold in the range, most units first: what the picker offers
   * and the table lists.
   */
  items: SkuSeriesItem[];
  /** The keys drawn, in order: the ones asked for, else the best sellers. */
  picked: string[];
  /**
   * One line per picked key, in the same order. A SKU that sold nothing in the
   * range is a line of zeros, not a missing one.
   */
  units: SkuSeriesLine[];
  amount: SkuSeriesLine[];
  /** Everything sold in range, not only the picked SKUs. */
  totals: { units: number; amount: Money };
}

/** One SKU's totals over the range: what the grouped query returns first. */
export type SkuTotalRow = SkuSeriesItem;

/** One SKU in one bucket: what the grouped query returns for the picked. */
export interface SkuBucketRow {
  /** The bucket's first day. */
  bucket: IsoDate;
  key: string;
  units: number;
  amount: Money;
}

/**
 * The key a line is grouped under: its SKU, else its own name.
 *
 * A line has no SKU of its own on most orders placed before the catalogue's
 * SKUs were set (#241), so the SKU is the line's, falling back to its
 * variant's; the caller has done that. What is left with neither — a custom
 * line, a variant deleted at the channel — still sold something, and groups by
 * name rather than being dropped from a best-sellers table.
 */
export function skuGroupKey(
  sku: string | null,
  productTitle: string,
  variantTitle: string,
): string {
  return sku ? `sku:${sku}` : `title:${productTitle}|${variantTitle}`;
}

/**
 * What a key says about its SKU, for one that sold nothing in the range and so
 * is not in `items`: its code, or its name and variant.
 */
export function skuKeyLabel(key: string): string {
  if (key.startsWith("sku:")) return key.slice(4);
  const [product = "", variant = ""] = key.replace(/^title:/, "").split("|");
  return variant ? `${product} · ${variant}` : product;
}

/** Shopify's name for the variant of a product that has only one. */
const DEFAULT_VARIANT = "Default Title";

/** A variant title as the view shows it: blank for the default. */
export function cleanVariantTitle(title: string | null | undefined): string {
  return !title || title === DEFAULT_VARIANT ? "" : title;
}

/** Most units first; ties by sales, then key, so no order depends on arrival. */
function bySales(a: SkuSeriesItem, b: SkuSeriesItem): number {
  if (b.units !== a.units) return b.units - a.units;
  const paise = toPaise(b.amount) - toPaise(a.amount);
  if (paise !== 0n) return paise > 0n ? 1 : -1;
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

/**
 * The keys to draw: those asked for (deduplicated, at most `MAX_SKU_PICKS`),
 * else the `DEFAULT_SKU_PICKS` best sellers.
 *
 * Shared because the API needs the answer before it reads the buckets, and the
 * assembly needs the same one after.
 */
export function pickSkuKeys(
  totals: readonly SkuTotalRow[],
  asked: readonly string[] | undefined,
): string[] {
  const keys = [...new Set(asked ?? [])].slice(0, MAX_SKU_PICKS);
  if (keys.length > 0) return keys;
  return [...totals]
    .sort(bySales)
    .slice(0, DEFAULT_SKU_PICKS)
    .map((item) => item.key);
}

/**
 * The view's data from grouped rows.
 *
 * `totals` is every SKU's totals; `rows` is the buckets of the picked SKUs
 * (rows of any other are ignored, so the fixture can pass them all). Throws on
 * a row whose bucket is not in the range's bucket list: that would mean the two
 * sides disagree about where a week starts, and dropping the row quietly is how
 * a chart ends up wrong.
 */
export function assembleSkuSeries(
  query: Pick<SkuSeriesQuery, "from" | "to" | "bucket" | "keys">,
  totals: readonly SkuTotalRow[],
  rows: readonly SkuBucketRow[],
  timezone: string = STORE_TIMEZONE,
): SkuSeries {
  const buckets = bucketStarts(query.from, query.to, query.bucket);
  const position = new Map(buckets.map((start, index) => [start, index]));
  const picked = pickSkuKeys(totals, query.keys);

  const lines = new Map(
    picked.map((key) => [
      key,
      {
        units: new Array<number>(buckets.length).fill(0),
        paise: new Array<bigint>(buckets.length).fill(0n),
        totalUnits: 0,
        totalPaise: 0n,
      },
    ]),
  );

  for (const row of rows) {
    const index = position.get(row.bucket);
    if (index === undefined) {
      throw new Error(
        `SKU series row for ${row.bucket} is outside ${query.from}..${query.to}`,
      );
    }
    const line = lines.get(row.key);
    if (!line) continue;
    const paise = toPaise(row.amount);
    line.units[index] = (line.units[index] ?? 0) + row.units;
    line.paise[index] = (line.paise[index] ?? 0n) + paise;
    line.totalUnits += row.units;
    line.totalPaise += paise;
  }

  let totalUnits = 0;
  let totalPaise = 0n;
  for (const item of totals) {
    totalUnits += item.units;
    totalPaise += toPaise(item.amount);
  }

  return {
    from: query.from,
    to: query.to,
    bucket: query.bucket,
    timezone,
    buckets,
    items: [...totals].sort(bySales),
    picked,
    units: picked.map((key) => {
      const line = lines.get(key);
      return {
        key,
        values: (line?.units ?? []).map(String),
        total: String(line?.totalUnits ?? 0),
      };
    }),
    amount: picked.map((key) => {
      const line = lines.get(key);
      return {
        key,
        values: (line?.paise ?? []).map(fromPaise),
        total: fromPaise(line?.totalPaise ?? 0n),
      };
    }),
    totals: { units: totalUnits, amount: fromPaise(totalPaise) },
  };
}

/** One order line, reduced to what the view counts. For the fixture adapter. */
export interface SkuSeriesPoint {
  placedAt: IsoDateTime;
  orderId: string;
  sku: string | null;
  productTitle: string;
  variantTitle: string;
  quantity: number;
  lineTotal: Money;
}

/**
 * Every SKU's totals, from order lines in memory — what Postgres' `group by`
 * returns on the API side.
 *
 * The caller has already applied the range and the cancelled switch; this only
 * groups. The same SKU can carry a different name on different lines (a
 * product renamed between two orders): the greater name wins, so the answer
 * does not depend on the order the lines arrive in.
 */
export function skuTotalsFromPoints(
  points: readonly SkuSeriesPoint[],
): SkuTotalRow[] {
  const groups = new Map<
    string,
    Omit<SkuTotalRow, "amount"> & { paise: bigint; orderIds: Set<string> }
  >();

  for (const point of points) {
    const key = skuGroupKey(point.sku, point.productTitle, point.variantTitle);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        sku: point.sku,
        productTitle: point.productTitle,
        variantTitle: point.variantTitle,
        units: 0,
        orders: 0,
        paise: 0n,
        orderIds: new Set(),
      };
      groups.set(key, group);
    }
    if (point.productTitle > group.productTitle) {
      group.productTitle = point.productTitle;
    }
    if (point.variantTitle > group.variantTitle) {
      group.variantTitle = point.variantTitle;
    }
    group.units += point.quantity;
    group.paise += toPaise(point.lineTotal);
    group.orderIds.add(point.orderId);
  }

  return [...groups.values()].map((group) => ({
    key: group.key,
    sku: group.sku,
    productTitle: group.productTitle,
    variantTitle: group.variantTitle,
    units: group.units,
    orders: group.orderIds.size,
    amount: fromPaise(group.paise),
  }));
}

/** Every SKU in every bucket, from order lines in memory. */
export function skuBucketRowsFromPoints(
  points: readonly SkuSeriesPoint[],
  bucket: OrderSeriesBucket,
  timezone: string = STORE_TIMEZONE,
): SkuBucketRow[] {
  const groups = new Map<
    string,
    { bucket: IsoDate; key: string; units: number; paise: bigint }
  >();

  for (const point of points) {
    const start = bucketStart(storeDate(point.placedAt, timezone), bucket);
    const key = skuGroupKey(point.sku, point.productTitle, point.variantTitle);
    const id = `${start}|${key}`;
    const group = groups.get(id) ?? { bucket: start, key, units: 0, paise: 0n };
    group.units += point.quantity;
    group.paise += toPaise(point.lineTotal);
    groups.set(id, group);
  }

  return [...groups.values()].map((group) => ({
    bucket: group.bucket,
    key: group.key,
    units: group.units,
    amount: fromPaise(group.paise),
  }));
}
